import { memo, ReactNode, useCallback, useRef, useState } from 'react';
import { ArdoqId } from '@ardoq/api-types';
import {
  ComponentMatrix2Commands,
  ViewpointModeComponentMatrixViewModel,
} from '../types';
import { type I18nLocale } from '@ardoq/i18n';
import {
  componentMatrixBody as ComponentMatrixBody,
  ComponentMatrixBodyRef,
} from './componentMatrixBody';
import { ComponentMatrixLevelHeadersArea } from './componentMatrixLevelHeadersArea';
import { ComponentMatrixColumnHeadersArea } from './componentMatrixColumnHeadersArea';
import {
  ComponentMatrixRowHeadersArea,
  ComponentMatrixRowHeadersAreaRef,
} from './componentMatrixRowHeadersArea';
import {
  ComponentMatrixMainContainer,
  resolveOtherLabelPopover,
} from '@ardoq/graph';
import { BulkHoverPopover } from '@ardoq/popovers';
import { usePreventWheelZoom, useResizeObserver } from '@ardoq/hooks';
import { RowHeadersResizeHandle } from './resizeHandle';
import { resolveTruncationPopover } from './truncationPopover';
import styled from 'styled-components';
import { s8 } from '@ardoq/design-tokens';

const resolveComponentMatrixPopover = (
  element: HTMLElement | SVGElement
): ReactNode | null =>
  resolveOtherLabelPopover(element) ?? resolveTruncationPopover(element);

const ComponentMatrix2MainContainer = styled(ComponentMatrixMainContainer)`
  row-gap: ${s8};
`;

type ComponentMatrixProperties = {
  viewModel: ViewpointModeComponentMatrixViewModel;
  i18nLocale: I18nLocale;
  contextComponentId: ArdoqId | null;
  hideRowHeadersResizeHandle?: boolean;
  commands: ComponentMatrix2Commands;
};
const ComponentMatrix = (props: ComponentMatrixProperties) => {
  const rowHeadersReference = useRef<ComponentMatrixRowHeadersAreaRef>(null);
  const columnHeadersReference = useRef<ComponentMatrixColumnHeadersArea>(null);
  const bodyReference = useRef<ComponentMatrixBodyRef>(null);
  const syncScroll = useCallback(
    (el: HTMLDivElement) => {
      if (!rowHeadersReference.current || !columnHeadersReference.current) {
        return;
      }
      const { scrollLeft, scrollTop } = el;
      rowHeadersReference.current.setScrollTop(scrollTop);
      columnHeadersReference.current.setScrollLeft(scrollLeft);
    },
    [rowHeadersReference, columnHeadersReference]
  );
  const [horizontalScrollbarHeight, setHorizontalScrollbarHeight] = useState(0);
  const containerReference = useRef<HTMLDivElement>(null);

  usePreventWheelZoom(containerReference);
  useResizeObserver(containerReference, () => {
    if (!bodyReference.current?.container.current) {
      return;
    }
    const currentHorizontalScrollbarHeight =
      (bodyReference.current.container.current?.offsetHeight ?? 0) -
      (bodyReference.current.container.current?.clientHeight ?? 0);
    if (currentHorizontalScrollbarHeight !== horizontalScrollbarHeight) {
      setHorizontalScrollbarHeight(currentHorizontalScrollbarHeight);
      syncScroll(bodyReference.current.container.current);
    }
  });

  const {
    viewModel,
    i18nLocale,
    contextComponentId,
    hideRowHeadersResizeHandle,
    commands,
  } = props;
  const {
    measurements,
    rowModels,
    hoveredComponentId,
    focusedComponentId,
    userAdjustedColumnWidths,
    userAdjustedRowHeights,
    resizeHandles,
  } = viewModel;
  const {
    mainContainerLeftColumnWidth,
    columnHeaderAndColumnWidths,
    rowHeights,
    leafRowKeys,
    leafRowMinHeights,
    rowHeadersAreaMinWidth,
  } = measurements;
  return (
    <ComponentMatrix2MainContainer
      ref={containerReference}
      onWheel={e => {
        if (!bodyReference.current) {
          return;
        }
        bodyReference.current.scrollBy(e.deltaX, e.deltaY);
      }}
      onClick={commands.onBackgroundClick}
    >
      <ComponentMatrixLevelHeadersArea
        viewModel={viewModel}
        i18nLocale={i18nLocale}
        commands={commands}
      />
      <ComponentMatrixColumnHeadersArea
        ref={columnHeadersReference}
        viewModel={viewModel}
        columnWidths={columnHeaderAndColumnWidths}
        contextComponentId={contextComponentId}
        commands={commands}
      />
      <ComponentMatrixRowHeadersArea
        ref={rowHeadersReference}
        rowModels={rowModels}
        rowHeights={rowHeights}
        horizontalScrollbarHeight={horizontalScrollbarHeight}
        width={mainContainerLeftColumnWidth}
        hoveredComponentId={hoveredComponentId}
        focusedComponentId={focusedComponentId}
        contextComponentId={contextComponentId}
        commands={commands}
      />
      <ComponentMatrixBody
        ref={bodyReference}
        commands={commands}
        rowModels={rowModels}
        rowHeights={rowHeights}
        columnWidths={columnHeaderAndColumnWidths}
        onScroll={e => syncScroll(e.currentTarget)}
        contextComponentId={contextComponentId}
        hoveredComponentId={hoveredComponentId}
        focusedComponentId={focusedComponentId}
        resizeHandles={resizeHandles}
        userAdjustedColumnWidths={userAdjustedColumnWidths}
        leafRowKeys={leafRowKeys}
        leafRowMinHeights={leafRowMinHeights}
        userAdjustedRowHeights={userAdjustedRowHeights}
      />
      {!hideRowHeadersResizeHandle && (
        <RowHeadersResizeHandle
          currentWidth={mainContainerLeftColumnWidth}
          minWidth={rowHeadersAreaMinWidth}
          onCommit={commands.onRowHeadersResize}
          onReset={commands.onRowHeadersResizeHandleDoubleClick}
        />
      )}
      <BulkHoverPopover
        rootRef={containerReference}
        resolveContent={resolveComponentMatrixPopover}
      />
    </ComponentMatrix2MainContainer>
  );
};

export const componentMatrix = memo(ComponentMatrix);
